import React, { useMemo, useState } from 'react';
import { Stack, Text, Box, Skeleton } from '@mantine/core';
import { useNavigation } from '../../contexts/NavigationContext';
import { useDashboardData } from '../../hooks/dashboard';
import { OverdueAnalysis } from './DashboardMode/components/OverdueAnalysis';
import { UpcomingDeadlines } from './DashboardMode/components/UpcomingDeadlines';
import { RecentActivity } from './DashboardMode/components/RecentActivity';
import { FocusAreas } from './DashboardMode/components/FocusAreas';
import { DashboardStats } from './DashboardMode/components/DashboardStats';
import { DashboardMasonry } from './DashboardMode/DashboardMasonry';
import { DashboardCard } from './DashboardMode/DashboardCard';
import { DashboardPreferencesProvider, useDashboardPreferences } from './DashboardMode/useDashboardPreferences';
import { OnboardingWelcome } from './DashboardMode/components/OnboardingWelcome';
import { OnboardingTasks, isOnboardingDismissed } from './DashboardMode/components/OnboardingTasks';
import { InsightsWidget } from './DashboardMode/components/InsightsWidget';
import { CommentsWidget } from './DashboardMode/components/CommentsWidget';
import { useUser } from '../../contexts/UserContext';
import { EditTodoForm, TaskTitleInput } from '../EditTodoForm';
import { Modal as CustomModal } from '../Modal';
import { useTodos, useUpdateTodo, useDeleteTodo } from '../../hooks/useTodos';
import { Check, Trash2, X } from 'lucide-react';
import { TodoItem, UpdateTodoRequest } from '../../types/todo';
import './DashboardMode.css';

const DashboardModeInner: React.FC = () => {
  const [onboardingDismissed, setOnboardingDismissed] = useState(isOnboardingDismissed());
  const [editingTodo, setEditingTodo] = useState<TodoItem | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateTodoRequest | null>(null);
  const { isMobile } = useNavigation();
  const { currentUser } = useUser();
  const dashboardData = useDashboardData();
  const { data: todos = [] } = useTodos();
  const updateTodo = useUpdateTodo();
  const deleteTodo = useDeleteTodo();

  // Derive upcoming tasks (next 7 days) since not part of dashboardData yet
  const upcomingTasksCount = useMemo(() => {
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return dashboardData.todos.filter(t => {
      if (!t.dueDate || t.isCompleted) return false;
      const due = new Date(t.dueDate);
      return due >= now && due <= sevenDaysFromNow;
    }).length;
  }, [dashboardData.todos]);

  // Progressive thresholds (v2 – matched to updated spec)
  const thresholds = useMemo(() => {
    if (dashboardData.isLoading) {
      return { hasAnyData: false, hasOverdue: false, hasUpcoming: false, hasRichData: false, hasSignificantActivity: false, hasMultipleLists: false, hasInsights: false, hasComments: false, isEmpty: false, showOnboarding: false };
    }
    const hasAnyData = dashboardData.todos.length > 0 || dashboardData.todoLists.length > 0 || dashboardData.notes.length > 0;
    const hasOverdue = dashboardData.overdueTasks > 0;
    const hasUpcoming = upcomingTasksCount >= 2; // Slightly lower to surface planning earlier
    const hasRichData = dashboardData.todos.length >= 8; // Increased from 3 to 8 for more meaningful insights
    const hasSignificantActivity = dashboardData.recentlyCompletedTasks.length >= 5;
    const hasMultipleLists = dashboardData.todoLists.length >= 3;
    const hasInsights = (dashboardData.todos.length + dashboardData.todoLists.length + dashboardData.notes.length) >= 2; // Low threshold for insights
    const hasComments = dashboardData.todos.some(task => task.comments && task.comments.length > 0); // Any task has comments

    // Show onboarding if no data OR insufficient data for meaningful dashboard insights
    const showOnboarding = !hasAnyData || (!hasRichData && !hasSignificantActivity && !hasMultipleLists);

    return { hasAnyData, hasOverdue, hasUpcoming, hasRichData, hasSignificantActivity, hasMultipleLists, hasInsights, hasComments, isEmpty: !hasAnyData, showOnboarding };
  }, [dashboardData, upcomingTasksCount, dashboardData.isLoading]);

  // Widget scoring (simple first pass – can be extracted later)
  interface WidgetDef { id: string; visible: boolean; score: number; render: () => JSX.Element | null; }

  const { hidden } = useDashboardPreferences(); // Hidden kept for backward compatibility but no UI now

  const handleEditTodo = (todoId: number) => {
    const todo = todos.find(t => t.id === todoId);
    if (todo) {
      setEditingTodo(todo);
    }
  };

  // After hooks, branch for loading UI using unified dashboardData.isLoading
  const isLoading = !!dashboardData.isLoading;

  const widgets: WidgetDef[] = useMemo(() => {
    const compactOverdueSummary = thresholds.hasOverdue ? (
      <div>
        <Text size="sm" c="dimmed">Overdue Tasks</Text>
        <Text size="xl" fw={600} c="red">{dashboardData.overdueTasks}</Text>
      </div>
    ) : null;
    const compactUpcomingSummary = thresholds.hasUpcoming ? (
      <div>
        <Text size="sm" c="dimmed">Next 7 Days</Text>
        <Text size="xl" fw={600} c="blue">{upcomingTasksCount}</Text>
      </div>
    ) : null;
    const compactActivitySummary = thresholds.hasSignificantActivity ? (
      <div>
        <Text size="sm" c="dimmed">Today</Text>
        <Text size="lg" fw={600}>{dashboardData.recentlyCompletedTasks.filter(t => t.completedAt && new Date(t.completedAt).toDateString() === new Date().toDateString()).length} done</Text>
      </div>
    ) : null;
    const compactFocusSummary = thresholds.hasMultipleLists ? (
      <div>
        <Text size="sm" c="dimmed">Active Lists</Text>
        <Text size="xl" fw={600}>{dashboardData.todoLists.length}</Text>
      </div>
    ) : null;
    const compactStatsSummary = thresholds.hasRichData ? (
      <div>
        <Text size="sm" c="dimmed">Completion</Text>
        <Text size="xl" fw={600}>{dashboardData.overallCompletionRate}%</Text>
      </div>
    ) : null;
    const compactInsightsSummary = thresholds.hasInsights ? (
      <div>
        <Text size="sm" c="dimmed">Activity</Text>
        <Text size="lg" fw={600}>
          {dashboardData.todos.length + dashboardData.todoLists.length + dashboardData.notes.length} items
        </Text>
      </div>
    ) : null;
    const compactCommentsSummary = thresholds.hasComments ? (
      <div>
        <Text size="sm" c="dimmed">Conversations</Text>
        <Text size="lg" fw={600}>
          {dashboardData.todos.filter(t => t.comments && t.comments.length > 0).length} tasks
        </Text>
      </div>
    ) : null;

    const arr: WidgetDef[] = [
      {
        id: 'onboarding',
        visible: thresholds.showOnboarding && !onboardingDismissed,
        score: 100, // Highest priority for new users
        render: () => (
          <DashboardCard
            id="onboarding"
            title=""
            summary={<OnboardingTasks onDismiss={() => setOnboardingDismissed(true)} />}
          />
        )
      },
      {
        id: 'overdue',
        visible: thresholds.hasOverdue,
        score: 90,
        render: () => (
          <DashboardCard id="overdue" title="Overdue" summary={compactOverdueSummary!} details={<OverdueAnalysis onEditTodo={handleEditTodo} />} />
        )
      },
      {
        id: 'upcoming',
        visible: thresholds.hasUpcoming,
        score: 80,
        render: () => (
          <DashboardCard id="upcoming" title="Upcoming" summary={compactUpcomingSummary!} details={<UpcomingDeadlines onEditTodo={handleEditTodo} />} />
        )
      },
      {
        id: 'activity',
        visible: thresholds.hasSignificantActivity,
        score: 60,
        render: () => (
          <DashboardCard id="activity" title="Activity" summary={compactActivitySummary!} details={<RecentActivity onEditTodo={handleEditTodo} />} />
        )
      },
      {
        id: 'focusAreas',
        visible: thresholds.hasMultipleLists,
        score: 55,
        render: () => (
          <DashboardCard id="focusAreas" title="Focus Areas" summary={compactFocusSummary!} details={<FocusAreas />} />
        )
      },
      {
        id: 'insights',
        visible: thresholds.hasInsights,
        score: 85, // High priority for early users to see interesting facts
        render: () => (
          <DashboardCard id="insights" title="Insights" summary={compactInsightsSummary!} details={<InsightsWidget onEditTodo={handleEditTodo} />} />
        )
      },
      {
        id: 'comments',
        visible: thresholds.hasComments,
        score: 70, // Between upcoming (80) and activity (60) - important for collaboration
        render: () => (
          <DashboardCard id="comments" title="Recent Comments" summary={compactCommentsSummary!} details={<CommentsWidget onEditTodo={handleEditTodo} />} />
        )
      },
      {
        id: 'stats',
        visible: thresholds.hasRichData,
  // Elevated score so Stats appears immediately after Quick Start allowing masonry to tuck it beneath when space permits
  score: 95,
        render: () => (
          <DashboardCard id="stats" title="Stats" summary={compactStatsSummary!} details={<DashboardStats />} />
        )
      }
    ];
    return arr
      .filter(w => w.visible && !hidden.includes(w.id))
      .sort((a, b) => b.score - a.score);
  }, [thresholds, dashboardData, upcomingTasksCount, hidden, onboardingDismissed]);

  // Decide render body without early returns to keep hook order stable
  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Stack gap="lg">
        <Box className="dashboard-greeting-wrapper">
          <div className="dashboard-greeting-surface" style={{ padding: '0.75rem 1.25rem', minWidth: 180 }}>
            <Skeleton height={18} width={140} radius="sm" />
          </div>
        </Box>
        <Box className="dashboard-skeleton-grid">
          {[1,2,3,4,5].map(i => (
            <Skeleton key={i} className="dashboard-skeleton-card" height={110} radius="md" />
          ))}
        </Box>
        <Text size="xs" c="dimmed" ta="center">Loading your dashboard…</Text>
      </Stack>
    );
  } else if (thresholds.isEmpty) {
    body = <OnboardingWelcome />;
  } else {
    body = (
      <Stack gap="lg">
        <Box className="dashboard-greeting-wrapper">
          <div className="dashboard-greeting-surface">
            <Text size="lg" fw={600} className="dashboard-greeting-text">
              Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}{currentUser?.name ? `, ${currentUser.name.split(' ')[0]}` : ''}
            </Text>
          </div>
        </Box>
        <DashboardMasonry items={widgets.map(w => ({ id: w.id, render: w.render }))} />
        <Box style={{ height: '1.5rem' }} />
      </Stack>
    );
  }

  return (
    <Box className="dashboard-mode-container">
      <Box p={isMobile ? (isLoading ? 'sm' : 'sm') : (isLoading ? 'md' : 'md')} style={{ width:'100%', margin:'0 auto', maxWidth:'100%' }}>
        {body}
      </Box>
      {editingTodo && (
        <CustomModal
          isOpen={!!editingTodo}
          onClose={() => {
            setEditingTodo(null);
            setEditFormData(null);
          }}
          hideCloseButton={true}
          headerContent={
            <TaskTitleInput
              value={editingTodo.title}
              onChange={(value) => {
                setEditingTodo(prev => prev ? { ...prev, title: value } : null);
                setEditFormData(prev => prev ? { ...prev, title: value } : { ...editingTodo, title: value });
              }}
              autoFocus={true}
              isCompleted={editingTodo.isCompleted}
              onToggleComplete={() => {
                const newCompletedState = !editingTodo.isCompleted;
                // Update local state immediately
                setEditingTodo(prev => prev ? { ...prev, isCompleted: newCompletedState } : null);
                // Update in backend
                updateTodo.mutate({
                  id: editingTodo.id,
                  data: { isCompleted: newCompletedState }
                }, {
                  onSuccess: () => {
                    // Close modal after longer delay if marking as complete
                    if (newCompletedState) {
                      setTimeout(() => {
                        setEditingTodo(null);
                        setEditFormData(null);
                      }, 600);
                    }
                  }
                });
              }}
            />
          }
          actionButtons={
            <>
              <button
                type="button"
                onClick={() => {
                  if (editFormData && editingTodo && editingTodo.id > 0) {
                    updateTodo.mutate({ id: editingTodo.id, data: editFormData }, {
                      onSuccess: () => {
                        setEditingTodo(null);
                        setEditFormData(null);
                      }
                    });
                  }
                }}
                disabled={!editFormData || !editFormData.title?.trim() || updateTodo.isPending || (editingTodo && editingTodo.id < 0)}
                className="header-btn header-btn-save"
                title={editingTodo && editingTodo.id < 0 ? "Please wait for task to be created..." : "Save task"}
                aria-label="Save task"
              >
                <Check size={16} />
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (editingTodo) {
                    deleteTodo.mutate(editingTodo.id, {
                      onSuccess: () => {
                        setEditingTodo(null);
                        setEditFormData(null);
                      }
                    });
                  }
                }}
                disabled={deleteTodo.isPending}
                className="header-btn header-btn-delete"
                title="Delete task"
                aria-label="Delete task"
              >
                <Trash2 size={16} />
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingTodo(null);
                  setEditFormData(null);
                }}
                className="header-btn header-btn-close"
                title="Cancel"
                aria-label="Cancel"
              >
                <X size={16} />
              </button>
            </>
          }
        >
          <EditTodoForm
            todo={editingTodo}
            onDataChange={setEditFormData}
            renderTitleInline={false}
          />
        </CustomModal>
      )}
    </Box>
  );
};

export const DashboardMode: React.FC = () => (
  <DashboardPreferencesProvider>
    <DashboardModeInner />
  </DashboardPreferencesProvider>
);